import React from "react";
import { throttle } from "throttle-debounce";
import Renderer from "../batchrenderer/Renderer";
import PropTypes from "prop-types";
import styled from "styled-components";

const OuterContainer = styled.div`
  transform: translateZ(0);
`;

const InnerContainer = styled.ul`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export default class GridView extends React.Component {
  state = {
    containerWidth: 0,
    itemWidth: 0,
    itemHeight: 0,
    itemMarginV: 0,
    itemMarginH: 0
  };

  static defaultProps = {
    // How much to inset the items left/right
    // BUG: Not currently accounting for small/zero inset that conflicts with the margin on the elements.
    inset: 38,
    itemsPerRow: 4,
    baseWidth: 230,
    baseHeight: 330,
    itemSpacingVert: 40,
    itemSpacingHoriz: 52,
    items: [],
    itemKeyProp: "",
    renderItem: null,
    initialBatch: 50,
    batchSize: 50,
    endBuffer: 500,
    minItemsPerRow: 2
  };

  static propTypes = {
    items: PropTypes.array.isRequired,
    renderItem: PropTypes.func.isRequired
  };

  container = null;

  componentDidMount() {
    window.addEventListener("resize", this.onResize);
    this.onResize();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);
  }

  getAvailableContentWidth(containerWidth) {
    // last 2 pixels for margins
    return containerWidth - this.props.inset * 2 - 2;
  }

  getTotalSpaceForItems(numItems) {
    return (
      numItems * this.props.baseWidth +
      (numItems - 1) * this.props.itemSpacingHoriz
    );
  }

  getItemsPerRow(availableContentWidth) {
    let i = this.props.minItemsPerRow;
    while (availableContentWidth > this.getTotalSpaceForItems(i)) {
      i++;
    }
    return i;
  }

  getLayoutParams(availableContentWidth, itemsPerRow) {
    let layoutParams = {};
    if (itemsPerRow === this.props.minItemsPerRow) {
      // use 90% of space for items, with margin in-between
      layoutParams = {
        itemWidth: Math.floor(
          (availableContentWidth * 0.9) / this.props.minItemsPerRow
        ),
        itemMarginH: Math.floor(availableContentWidth * 0.1) / 2
      };
    } else {
      layoutParams = {
        itemWidth: this.props.baseWidth,
        itemMarginH: this.props.itemSpacingHoriz / 2
      };
    }

    layoutParams.itemHeight = Math.floor(
      (this.props.baseHeight / this.props.baseWidth) * layoutParams.itemWidth
    );
    layoutParams.itemMarginV = Math.floor(
      (this.props.itemSpacingVert *
        (this.props.baseWidth / layoutParams.itemWidth)) /
        2
    );
    return layoutParams;
  }

  onResize = throttle(200, () => {
    this.setSizeState(this.container.offsetWidth);
  });

  setSizeState(containerWidth) {
    // get the available width for items
    let availableContentWidth = this.getAvailableContentWidth(containerWidth);

    // calculate how many items will fit
    let itemsPerRow = this.getItemsPerRow(availableContentWidth);

    // calculate item dimensions and spacing
    let layoutParams = this.getLayoutParams(availableContentWidth, itemsPerRow);

    // draw items
    this.setState({ ...layoutParams, containerWidth });
  }

  getItem = i => {
    const item = this.props.items[i];
    const outputProps = {
      index: i,
      item,
      width: this.state.itemWidth,
      height: this.state.itemHeight
    };

    let key = null;
    if (this.props.itemKeyProp) {
      key = item[this.props.itemKeyProp];
    } else if (typeof item === "string" || typeof item === "number") {
      key = item;
    }

    return (
      <li
        key={key}
        style={{
          // Force a fixed number of items per row to prevent the throttled
          // resize leading to changing wrapping
          width: `${this.state.itemWidth}px`,
          height: `${this.state.itemHeight}px`,
          margin: `${this.state.itemMarginV}px ${this.state.itemMarginH}px`
        }}
      >
        {this.props.renderItem(outputProps)}
      </li>
    );
  };

  render() {
    return (
      <OuterContainer
        innerRef={el => (this.container = el)}
        style={{ padding: `0 ${this.props.inset}px` }}
      >
        <div
          style={{
            margin: `-${this.state.itemMarginV}px -${this.state.itemMarginH}px`,
          }}
        >
          <Renderer
            total={this.props.items.length}
            initialBatch={this.props.initialBatch}
            batchSize={this.props.batchSize}
            endBuffer={this.props.endBuffer}
            renderItem={this.getItem}
            container={InnerContainer}
          />
        </div>
      </OuterContainer>
    );
  }
}
